// src/pages/SiteManager.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import "./styles/SiteManager1.css";
import { AuthContext } from "../context/AuthContext";

export default function SiteManager({ onSiteCreated }) {
  const { user } = useContext(AuthContext);
  const role = user?.role;
  const isAdmin = ["admin", "super-admin"].includes(role);

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const [sites, setSites] = useState([]);          // shown list
  const [allSites, setAllSites] = useState([]);    // master list for client search fallback
  const [masterSites, setMasterSites] = useState([]);

  const [form, setForm] = useState({
    name: "",
    phoneNumber: "",
    dispenserType: "",
    latitude: "",
    longitude: "",
    masterSiteId: "",
  });

  const [editingId, setEditingId] = useState(null);

  // ✅ Search
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedMasterSite = useMemo(
    () => masterSites.find((ms) => ms._id === form.masterSiteId),
    [masterSites, form.masterSiteId]
  );

  // For faster matching master site names during search
  const masterNameById = useMemo(() => {
    const map = {};
    for (const ms of masterSites) map[ms._id] = ms.name || "";
    return map;
  }, [masterSites]);

  const clientFilter = (list, query) => {
    const term = String(query || "").trim().toLowerCase();
    if (!term) return list;

    return (list || []).filter((s) => {
      const siteName = (s.name || "").toLowerCase();
      const phone = (s.phoneNumber || "").toLowerCase();
      const masterName = (masterNameById[s.masterSiteId] || "").toLowerCase();
      const dispenser = (s.dispenserType || "").toLowerCase();
      const lat = String(s.latitude || "").toLowerCase();
      const lng = String(s.longitude || "").toLowerCase();

      return (
        siteName.includes(term) ||
        phone.includes(term) ||
        masterName.includes(term) ||
        dispenser.includes(term) ||
        lat.includes(term) ||
        lng.includes(term)
      );
    });
  };

  const fetchSites = async (queryOverride) => {
    try {
      setLoading(true);
      setError("");

      const query = typeof queryOverride === "string" ? queryOverride : q;

      // 1) Always keep a fresh full list
      const allRes = await axios.get("/api/sites", authHeader);
      const fullList = allRes.data || [];
      setAllSites(fullList);

      // If empty query -> show all
      if (!query || !query.trim()) {
        setSites(fullList);
        return;
      }

      // 2) Try backend search if it exists
      let serverList = null;
      try {
        const url = `/api/sites?q=${encodeURIComponent(query)}`;
        const res = await axios.get(url, authHeader);
        serverList = res.data || null;
      } catch {
        serverList = null;
      }

      // 3) Decide if server search actually filtered
      const serverLooksFiltered =
        Array.isArray(serverList) && serverList.length <= fullList.length - 1;

      if (serverLooksFiltered) {
        setSites(serverList);
      } else {
        // Fallback: client filter (works always)
        setSites(clientFilter(fullList, query));
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to fetch sites");
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterSites = async () => {
    try {
      const res = await axios.get("/api/mastersites", authHeader);
      setMasterSites(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to fetch master sites");
    }
  };

  useEffect(() => {
    fetchSites("");
    fetchMasterSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm({
      name: "",
      phoneNumber: "",
      dispenserType: "",
      latitude: "",
      longitude: "",
      masterSiteId: "",
    });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;

    try {
      setLoading(true);
      setError("");

      const payload = {
        ...form,
        masterSiteId: form.masterSiteId || "",
      };

      if (editingId) {
        await axios.put(`/api/sites/${editingId}`, payload, authHeader);
      } else {
        await axios.post("/api/sites", payload, authHeader);
        if (onSiteCreated) onSiteCreated();
      }

      resetForm();
      await fetchSites(q); // keep search applied after save
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to save site");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (site) => {
    setEditingId(site._id);
    setForm({
      name: site.name || "",
      phoneNumber: site.phoneNumber || "",
      dispenserType: site.dispenserType || "",
      latitude: site.latitude || "",
      longitude: site.longitude || "",
      masterSiteId: site.masterSiteId || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!isAdmin) return;
    if (!window.confirm("Delete this site?")) return;

    try {
      setLoading(true);
      setError("");
      await axios.delete(`/api/sites/${id}`, authHeader);
      await fetchSites(q); // keep search applied after delete
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to delete site");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="site-manager-container">
      {/* ✅ TOP HEADER: title + search */}
      <div className="list-header">
        <div className="title-stack">
          <h2 className="site-list-title">Sites</h2>
          <div className="subtle-hint">Search, add, and manage site records.</div>
        </div>

        <div className="search-wrap">
          <input
            type="text"
            placeholder="Search site name / phone / master..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") fetchSites();
            }}
          />
          <button onClick={() => fetchSites()} disabled={loading}>
            Search
          </button>
          <button
            onClick={() => {
              setQ("");
              setSites(allSites); // instant reset
            }}
            disabled={loading}
          >
            Reset
          </button>
        </div>
      </div>

      {error && <div className="error-box">{error}</div>}

      {/* ✅ TABLE */}
      <div className="table-card">
        <div className="table-scroll">
          <table className="site-list">
            <thead>
              <tr>
                <th className="col-name">Site Name</th>
                <th className="col-phone">Phone</th>
                {isAdmin && <th className="col-lat">Latitude</th>}
                {isAdmin && <th className="col-lng">Longitude</th>}
                {isAdmin && <th className="col-disp">Dispenser</th>}
                <th className="col-master">Master Site</th>
                {isAdmin && <th className="col-actions">Actions</th>}
              </tr>
            </thead>

            <tbody>
              {sites.map((site) => {
                const master = masterSites.find((m) => m._id === site.masterSiteId);
                return (
                  <tr key={site._id}>
                    <td className="col-name">{site.name}</td>
                    <td className="col-phone">{site.phoneNumber || "-"}</td>

                    {isAdmin && <td className="col-lat">{site.latitude || "-"}</td>}
                    {isAdmin && <td className="col-lng">{site.longitude || "-"}</td>}
                    {isAdmin && <td className="col-disp">{site.dispenserType || "-"}</td>}

                    <td className="col-master">{master?.name || "—"}</td>

                    {isAdmin && (
                      <td className="col-actions">
                        <div className="action-buttons">
                          <button className="edit-btn" onClick={() => handleEdit(site)}>
                            Edit
                          </button>
                          <button className="delete-btn" onClick={() => handleDelete(site._id)}>
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}

              {sites.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 7 : 3} className="empty-row">
                    No sites found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ✅ FORM */}
      {isAdmin && (
        <div className="site-form">
          <h2>{editingId ? "Edit Site" : "Add Site"}</h2>

          <form onSubmit={handleSubmit}>
            <input
              type="text"
              placeholder="Site Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />

            <input
              type="text"
              placeholder="Phone Number"
              value={form.phoneNumber}
              onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
            />

            <input
              type="text"
              placeholder="Latitude"
              value={form.latitude}
              onChange={(e) => setForm({ ...form, latitude: e.target.value })}
            />

            <input
              type="text"
              placeholder="Longitude"
              value={form.longitude}
              onChange={(e) => setForm({ ...form, longitude: e.target.value })}
            />

            <select
              value={form.masterSiteId || ""}
              onChange={(e) => {
                const selectedId = e.target.value;
                const master = masterSites.find((ms) => ms._id === selectedId);
                setForm((prev) => ({
                  ...prev,
                  masterSiteId: selectedId,
                  dispenserType: master?.dispenserType || "",
                }));
              }}
            >
              <option value="">Select Master Site</option>
              {masterSites.map((ms) => (
                <option key={ms._id} value={ms._id}>
                  {ms.name}
                </option>
              ))}
            </select>

            {selectedMasterSite && (
              <div className="site-info">
                <strong>Dispenser Type:</strong> {selectedMasterSite.dispenserType}
              </div>
            )}

            <button type="submit" disabled={loading}>
              {editingId ? "Update" : "Add"}
            </button>

            {editingId && (
              <button type="button" className="secondary-btn" onClick={resetForm} disabled={loading}>
                Cancel
              </button>
            )}
          </form>
        </div>
      )}

      {loading && <div className="loading">Loading...</div>}
    </div>
  );
}